// The Parking and Storage sheets' draft (MD-2A): the shared sheet engine with the space fields.
import {
  SPACE_FIELDS,
  type SpaceBatchRow,
  type SpaceClearable,
  type SpaceField,
  type SpaceKind,
  type SpaceSheetRow,
  type StructureListResponse,
} from '@sivan/shared';
import { type SheetRow as GenericRow, draftEngine } from './sheet-draft';

export type SpaceRow = GenericRow<SpaceField, SpaceClearable>;
export interface SpaceContext {
  readonly kind: SpaceKind;
  readonly structures: StructureListResponse['items'];
}

const CLEAR_KEY: Readonly<Record<SpaceField, SpaceClearable | null>> = {
  location: null,
  number: null,
  level: 'level',
  building: 'apartment',
  apartment: 'apartment',
};

/** Structures a space of this kind may be in (never the other kind's dedicated structure). */
export const locationsFor = (context: SpaceContext) =>
  context.structures.filter((s) => s.kind !== (context.kind === 'parking' ? 'storage' : 'parking'));

/** Immediate feedback with the rules the server applies (it re-validates everything). */
export function validateSpace(row: SpaceRow, context: SpaceContext): Record<string, string> {
  const errors: Record<string, string> = {};
  const value = (f: SpaceField) => engine.displayValue(row, f).trim();
  for (const [field, max] of [
    ['location', 12],
    ['level', 40],
    ['number', 12],
    ['building', 12],
    ['apartment', 12],
  ] as const) {
    if (row.edits[field] !== undefined && value(field).length > max) errors[field] = 'too_long';
  }
  if (row.unitId === null) {
    if (value('location') === '') errors['location'] ??= 'required';
    if (value('number') === '') errors['number'] ??= 'required';
  }
  const code = value('location').toLowerCase();
  const structure = locationsFor(context).find((s) => s.code.toLowerCase() === code);
  if (row.edits.location !== undefined && structure === undefined)
    errors['location'] ??= 'unknown_location';
  const level = value('level');
  if (structure !== undefined && row.edits.level !== undefined && level !== '') {
    const matches = structure.levels.filter(
      (l) => l.label.toLowerCase() === level.toLowerCase() || String(l.number) === level,
    );
    if (matches.length === 0) errors['level'] ??= 'unknown_level';
  }
  if (row.edits.building !== undefined) {
    const buildings = context.structures.filter(
      (s) => s.kind !== 'parking' && s.kind !== 'storage',
    );
    if (!buildings.some((s) => s.code.toLowerCase() === value('building').toLowerCase()))
      errors['building'] ??= 'unknown_building';
  }
  if (row.edits.apartment !== undefined && value('building') === '')
    errors['building'] ??= 'required';
  return errors;
}

const engine = draftEngine<SpaceField, SpaceClearable, SpaceContext>({
  columns: [...SPACE_FIELDS],
  isField: (key): key is SpaceField => (SPACE_FIELDS as readonly string[]).includes(key),
  clearKey: (field) => CLEAR_KEY[field],
  validate: validateSpace,
});

export const spaceDraft = engine;

export const toSpaceBatchRows = (rows: readonly SpaceRow[]): SpaceBatchRow[] =>
  engine.toBatchRows(rows);

export function rowFromSpace(space: SpaceSheetRow): SpaceRow {
  return {
    ...engine.newRow(),
    key: space.unitId,
    unitId: space.unitId,
    rowVersion: space.rowVersion,
    base: {
      location: space.location,
      level: space.level,
      number: space.number,
      building: space.allocation?.building ?? '',
      apartment: space.allocation?.apartment ?? '',
    },
    // A future allocation or a scheduled end: assignment changes wait for a later milestone.
    locked: space.scheduled ? { building: 'space_scheduled', apartment: 'space_scheduled' } : {},
  };
}

export const rowsFromSpaces = (items: readonly SpaceSheetRow[]): SpaceRow[] => [
  ...items.map(rowFromSpace),
  engine.newRow(),
];
